// src/components/admin/settings/GeneralSettings.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Globe } from 'lucide-react';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import { updatePlatformInfo, selectPlatformInfo } from '../../../store/slices/platformSettingsSlice';
import toast from 'react-hot-toast';

const GeneralSettings = () => {
  const dispatch = useDispatch();
  const platformInfoRedux = useSelector(selectPlatformInfo);

  const { translatedText: generalSettings } = useTranslation('General Settings');
  const { translatedText: platformName } = useTranslation('Platform Name');
  const { translatedText: platformDescription } = useTranslation('Description');
  const { translatedText: mission } = useTranslation('Mission');
  const { translatedText: vision } = useTranslation('Vision');
  const { translatedText: contactEmail } = useTranslation('Contact Email');
  const { translatedText: contactPhone } = useTranslation('Contact Phone');
  const { translatedText: address } = useTranslation('Address');
  const { translatedText: street } = useTranslation('Street');
  const { translatedText: city } = useTranslation('City');
  const { translatedText: state } = useTranslation('State');
  const { translatedText: country } = useTranslation('Country');
  const { translatedText: zipCode } = useTranslation('Zip Code');

  const [localInfo, setLocalInfo] = React.useState(
    platformInfoRedux || {
      name: '',
      description: '',
      mission: '',
      vision: '',
      contactEmail: '',
      contactPhone: '',
      address: { street: '', city: '', state: '', country: '', zipCode: '' }
    }
  );

  React.useEffect(() => {
    if (platformInfoRedux) {
      setLocalInfo(platformInfoRedux);
    }
  }, [platformInfoRedux]);

  const handleChange = (field, value) => {
    setLocalInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setLocalInfo(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const handleSave = async () => {
    try {
      await dispatch(updatePlatformInfo(localInfo)).unwrap();
      toast.success('General settings saved!');
    } catch (error) {
      toast.error(error || 'Failed to save settings');
    }
  };

  return (
    <div className={styles.settingsCard}>
      <h3>
        <Globe size={20} />
        {generalSettings || 'General Settings'}
      </h3>
      <div className={styles.settingsForm}>
        <div className={styles.formGroup}>
          <label>{platformName || 'Platform Name'}</label>
          <input
            type="text"
            value={localInfo.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter platform name"
          />
        </div>
        <div className={styles.formGroup}>
          <label>{platformDescription || 'Description'}</label>
          <textarea
            value={localInfo.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            placeholder="Enter platform description"
          />
        </div>
        <div className={styles.formGroup}>
          <label>{mission || 'Mission'}</label>
          <textarea
            value={localInfo.mission}
            onChange={(e) => handleChange('mission', e.target.value)}
            rows={2}
            placeholder="Enter platform mission"
          />
        </div>
        <div className={styles.formGroup}>
          <label>{vision || 'Vision'}</label>
          <textarea
            value={localInfo.vision}
            onChange={(e) => handleChange('vision', e.target.value)}
            rows={2}
            placeholder="Enter platform vision"
          />
        </div>
        <div className={styles.formGroup}>
          <label>{contactEmail || 'Contact Email'}</label>
          <input
            type="email"
            value={localInfo.contactEmail}
            onChange={(e) => handleChange('contactEmail', e.target.value)}
            placeholder="contact@example.com"
          />
        </div>
        <div className={styles.formGroup}>
          <label>{contactPhone || 'Contact Phone'}</label>
          <input
            type="tel"
            value={localInfo.contactPhone}
            onChange={(e) => handleChange('contactPhone', e.target.value)}
            placeholder="+509 0000-0000"
          />
        </div>
        
        <h4 className={styles.subsectionTitle}>{address || 'Address'}</h4>
        <div className={styles.formGroup}>
          <label>{street || 'Street'}</label>
          <input
            type="text"
            value={localInfo.address.street}
            onChange={(e) => handleAddressChange('street', e.target.value)}
            placeholder="Street address"
          />
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{city || 'City'}</label>
            <input
              type="text"
              value={localInfo.address.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              placeholder="City"
            />
          </div>
          <div className={styles.formGroup}>
            <label>{state || 'State'}</label>
            <input
              type="text"
              value={localInfo.address.state}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              placeholder="State"
            />
          </div>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{country || 'Country'}</label>
            <input
              type="text"
              value={localInfo.address.country}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              placeholder="Country"
            />
          </div>
          <div className={styles.formGroup}>
            <label>{zipCode || 'Zip Code'}</label>
            <input
              type="text"
              value={localInfo.address.zipCode}
              onChange={(e) => handleAddressChange('zipCode', e.target.value)}
              placeholder="Zip code"
            />
          </div>
        </div>
        <button className={styles.saveBtn} onClick={handleSave}>Save Changes</button>
      </div>
    </div>
  );
};

export default GeneralSettings;